<script>
import BaseView from "@/views/BaseView.vue";
import { mapState } from "pinia";
import { useAppStore } from "@/store/app.store";
import ValidationMixin from "@/mixins/validation.mixin";
import LogoIcon from "@/assets/images/logo.svg";

export default {
  name: "LoginPage",
  components: { LogoIcon },
  mixins: [BaseView, ValidationMixin],
  data() {
    return {
      isFormValid: false,
      formError: "",
      email: "",
      password: "",
      showPass: false,
    };
  },
  computed: {
    ...mapState(useAppStore, ["mobile"]),
  },
  created() {
    this.setTitle("Welcome to Omni-dispatch TMS");
  },
  methods: {
    async signIn() {
      this.reset();
      if (!this.isFormValid) {
        return;
      }
      const resp = await this.$api.auth.login({ email: this.email, password: this.password });
      if (resp.success && resp.data?.token) {
        this.$api.client.saveToken(resp.data.token);
        const appStore = useAppStore();
        appStore.setDispatcher(resp.data.dispatcher);

        return this.$router.push({ name: "chat" });
      } else {
        if (resp.status === 400) {
          this.formError = "Wrong Email or password";
          return;
        }
        this.formError = `Server error (code: ${resp.error?.response?.status})`;
      }
    },
    reset() {
      this.formError = "";
      this.$api.client.setToken("");
    },
  },
};
</script>

<template>
  <v-layout class="login-form pa-0 align-center justify-center" full-height>
    <div class="pa-7 login-form__inner">
      <div class="login-form__top pt-10 pr-6 pb-8 pl-6">
        <div class="mb-8">
          <div class="login-form__logo mb-4">
            <logo-icon />
          </div>
          <div class="login-form__title text-h5 text-center font-weight-medium mb-2">Welcome to Omni-Dispatch TMS</div>
          <div class="login-form__subtitle text-grey-darken-1 text-body-2 text-center mb-0">Log in to your account</div>
        </div>

        <v-form v-model="isFormValid" @submit.prevent="signIn">
          <v-card variant="flat">
            <v-card-text class="px-0">
              <v-text-field
                v-model="email"
                :rules="[requiredValidator]"
                class="mb-5"
                color="primary"
                density="compact"
                hide-details="auto"
                label="E-mail"
                required
                type="email"
                variant="outlined"
              />
              <v-text-field
                v-model="password"
                :append-inner-icon="showPass ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                :rules="[requiredValidator]"
                :type="showPass ? 'text' : 'password'"
                class="mb-4"
                color="primary"
                density="compact"
                hide-details="auto"
                label="Password"
                required
                variant="outlined"
                @click:append-inner="showPass = !showPass"
              />
              <v-slide-y-transition>
                <div v-if="formError" class="text-danger font-size-12">
                  {{ formError }}
                </div>
              </v-slide-y-transition>
            </v-card-text>
            <v-card-actions class="pa-0">
              <v-btn
                class="mb-4 width-100 text-uppercase font-weight-600"
                color="primary"
                height="45"
                type="submit"
                variant="flat"
              >
                Log in
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
        <div class="subtitle">
          <router-link
            :to="{ name: 'reset-password' }"
            class="pa-1 text-uppercase text-primary text-decoration-none font-weight-600 text-center d-block font-size-12"
          >
            Forgot password
          </router-link>
        </div>
      </div>

      <div class="login-form__bottom">
        <div class="d-flex align-center font-weight-medium">
          <span class="mr-4 text-grey-darken-3">Support:</span>
          <a class="text-primary" href="mailto:info@omni-dispatch.com">info@omni-dispatch.com</a>
        </div>
      </div>
    </div>
  </v-layout>
</template>

<style lang="scss" scoped>
.form {
  background-color: #f5f5ff;
}

.login-form__inner {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .login-form__top {
    width: 430px;
    margin: 0 auto;
    box-shadow:
      0 3px 1px -2px rgba(0, 0, 0, 0.2),
      0px 2px 2px rgba(0, 0, 0, 0.14),
      0px 1px 5px rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    background: #fff;
  }
}

.login-form__logo {
  max-width: 50px;
  margin: 0 auto;
}

.login-form__bottom {
  position: absolute;
  bottom: 35px;
}
</style>

<style lang="scss">
.login-form__inner {
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    transition: background-color 5000s;
  }
}

.login-form__inner {
  .v-text-field--outlined.v-input--dense .v-label {
    top: 50%;
    transform: translateY(-50%);
  }

  .v-text-field--outlined.v-input--dense .v-label--active {
    top: 0;
    transform: translateY(-8px) scale(0.75);
  }

  .v-input__append-inner {
    align-self: center;
    margin-top: 0 !important;
  }
}
</style>
